/**
 * Sprint 24.13 (S24.13.11) — owner-only prompt-cache health dashboard.
 *
 * Surfaces whether the Sprint 24.6–24.10 cache work is actually paying off:
 * average cache-hit ratio over the period (+ delta vs the prior period), an
 * hour-bucketed stacked-area time series (cached vs uncached input tokens — the
 * green share IS the hit ratio), and the top conversations by token spend with
 * their per-conversation ratios.
 *
 * Owner-only at two layers: this route is guarded in App.tsx, and the backing
 * `get_cache_health` tool is in OWNER_ONLY_TOOLS.
 *
 * Chart is hand-rolled SVG to match the repo convention (no chart library is a
 * dependency — the spec's "Recharts already in repo" premise was wrong; pulling
 * one in for a single owner-only page would contradict this sprint's minimalism).
 */

import { type CSSProperties } from "react";
import { useCacheHealth } from "../hooks/use-cache-health";
import { useChatScope } from "../hooks/use-chat-scope";
import type { CacheHealthBucket, CacheHealthResult } from "../../lib/types";

const DAYS = 7;

function bandColor(ratio: number): string {
  return ratio >= 0.8 ? "var(--color-success)" : ratio >= 0.4 ? "var(--color-warning)" : "var(--color-error)";
}

function pct(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}

export function CacheHealthPage() {
  useChatScope({ kind: "workspace", label: "Cache health" }, null, []);
  const { data, isLoading, isError, refetch, isFetching } = useCacheHealth(DAYS);

  return (
    <div style={container}>
      <div style={headerRow}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>Cache health</h1>
          <p style={{ fontSize: 13, color: "var(--color-text-secondary)", margin: "4px 0 0" }}>
            Prompt-cache performance · last {DAYS} days
          </p>
        </div>
        <button type="button" style={refreshBtn} onClick={() => void refetch()} disabled={isFetching}>
          <i className="ti ti-refresh" style={{ fontSize: 14 }} aria-hidden="true" />
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {isLoading && <div style={muted}>Loading cache health…</div>}
      {isError && (
        <div style={errorBox}>
          Couldn’t load cache health. This view is owner-only — if you’re a member, that’s expected.
        </div>
      )}
      {!isLoading && !isError && data && <CacheHealthBody data={data} />}
    </div>
  );
}

function CacheHealthBody({ data }: { data: CacheHealthResult }) {
  const hasData = data.turn_count > 0;
  const deltaPP = Math.round((data.avg_cache_hit_ratio - data.prior_avg_cache_hit_ratio) * 100);
  const hasPrior = data.prior_avg_cache_hit_ratio > 0;

  if (!hasData) {
    return (
      <div style={emptyHint}>
        No cached chat turns in the last {data.days} days yet. Send a few chat messages — the cache primes on
        the first turn and the ratio appears from the second turn on.
      </div>
    );
  }

  return (
    <>
      {/* Headline */}
      <div style={headlineRow}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <span style={{ ...headlineNumber, color: bandColor(data.avg_cache_hit_ratio) }}>
            {pct(data.avg_cache_hit_ratio)}
          </span>
          <span style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>avg cache hit</span>
        </div>
        {hasPrior ? (
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: deltaPP > 0 ? "var(--color-success)" : deltaPP < 0 ? "var(--color-error)" : "var(--color-text-tertiary)",
            }}
            title={`Versus the prior ${data.days} days (${pct(data.prior_avg_cache_hit_ratio)})`}
          >
            {deltaPP > 0 ? "▲" : deltaPP < 0 ? "▼" : "—"} {deltaPP > 0 ? "+" : ""}
            {deltaPP}pp
          </span>
        ) : (
          <span style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>no prior period</span>
        )}
      </div>

      {/* Summary stats */}
      <div style={statsRow}>
        <Stat label="Cached tokens" value={data.total_cached_tokens.toLocaleString()} />
        <Stat label="Uncached tokens" value={data.total_uncached_tokens.toLocaleString()} />
        <Stat label="Total input" value={data.total_input_tokens.toLocaleString()} />
        <Stat label="Turns" value={data.turn_count.toLocaleString()} />
        <Stat label="Conversations" value={data.conversation_count.toLocaleString()} />
      </div>

      {/* Time series */}
      <h2 style={sectionHeading}>Cached vs uncached input, by hour</h2>
      <StackedAreaChart buckets={data.by_hour} />
      <div style={legendRow}>
        <span style={legendItem}><span style={{ ...swatch, background: "var(--color-success)" }} /> Cached</span>
        <span style={legendItem}><span style={{ ...swatch, background: "var(--color-text-tertiary)", opacity: 0.4 }} /> Uncached</span>
      </div>

      {/* Top conversations */}
      <h2 style={sectionHeading}>Top conversations by token spend</h2>
      {data.top_conversations.length === 0 ? (
        <div style={muted}>No conversations in this window.</div>
      ) : (
        <table style={table}>
          <thead>
            <tr>
              <th style={thLeft}>Conversation</th>
              <th style={thRight}>Turns</th>
              <th style={thRight}>Input tokens</th>
              <th style={thRight}>Cached</th>
              <th style={thRight}>Hit ratio</th>
              {/* Sprint 24.14 — silent provider fallback turns (0 in the normal case). */}
              <th style={thRight}>Fallback</th>
              <th style={thRight}>Last activity</th>
            </tr>
          </thead>
          <tbody>
            {data.top_conversations.map((c) => (
              <tr key={c.conversation_id} style={tr}>
                <td style={tdLeft} title={c.conversation_id}>{c.scope_label}</td>
                <td style={tdRight}>{c.turn_count.toLocaleString()}</td>
                <td style={tdRight}>{c.total_tokens.toLocaleString()}</td>
                <td style={tdRight}>{c.total_cached_tokens.toLocaleString()}</td>
                <td style={{ ...tdRight, color: bandColor(c.cache_hit_ratio), fontWeight: 500 }}>
                  {pct(c.cache_hit_ratio)}
                </td>
                <td
                  style={{
                    ...tdRight,
                    color: (c.fallback_turns ?? 0) > 0 ? "var(--color-warning, #b45309)" : "var(--color-text-tertiary)",
                    fontWeight: (c.fallback_turns ?? 0) > 0 ? 500 : 400,
                  }}
                  title="Turns served by a fallback provider (silent provider failover)"
                >
                  {(c.fallback_turns ?? 0).toLocaleString()}/{c.turn_count.toLocaleString()}
                </td>
                <td style={{ ...tdRight, color: "var(--color-text-tertiary)" }}>{formatWhen(c.last_activity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={statCard}>
      <div style={{ fontSize: 18, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>{label}</div>
    </div>
  );
}

/**
 * Hand-rolled stacked-area chart: cached tokens (green) stacked under uncached
 * (muted). x = hour bucket by index; y = tokens. The green share of each column
 * is the per-hour hit ratio. viewBox is responsive (width:100%).
 */
function StackedAreaChart({ buckets }: { buckets: CacheHealthBucket[] }) {
  if (buckets.length === 0) {
    return <div style={muted}>No cache activity in this window.</div>;
  }
  const W = 720;
  const H = 180;
  const PAD = 4;
  const maxTotal = Math.max(1, ...buckets.map((b) => b.total_tokens));
  const n = buckets.length;
  const xAt = (i: number) => (n === 1 ? W / 2 : PAD + (i / (n - 1)) * (W - PAD * 2));
  const yAt = (v: number) => H - PAD - (v / maxTotal) * (H - PAD * 2);

  // Cached area: baseline → cached. Uncached area: cached → total (stacked on top).
  const cachedTop = buckets.map((b, i) => `${xAt(i).toFixed(1)},${yAt(b.cached_tokens).toFixed(1)}`);
  const totalTop = buckets.map((b, i) => `${xAt(i).toFixed(1)},${yAt(b.total_tokens).toFixed(1)}`);
  const baseline = `${xAt(n - 1).toFixed(1)},${(H - PAD).toFixed(1)} ${xAt(0).toFixed(1)},${(H - PAD).toFixed(1)}`;

  const cachedPoly = `${cachedTop.join(" ")} ${baseline}`;
  // Uncached polygon: top edge (total) forward, then cached edge backward.
  const uncachedPoly = `${totalTop.join(" ")} ${[...cachedTop].reverse().join(" ")}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={chartSvg} role="img" aria-label="Cached vs uncached input tokens by hour">
      <polygon points={uncachedPoly} fill="var(--color-text-tertiary)" opacity={0.25} />
      <polygon points={cachedPoly} fill="var(--color-success)" opacity={0.55} />
      <polyline points={totalTop.join(" ")} fill="none" stroke="var(--color-border-secondary)" strokeWidth={1} opacity={0.5} />
    </svg>
  );
}

function formatWhen(ts: string): string {
  // SQLite UTC datetime → local short. Tolerate already-ISO strings.
  const ms = Date.parse(ts.includes("T") ? ts : ts.replace(" ", "T") + "Z");
  if (Number.isNaN(ms)) return ts;
  return new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const container: CSSProperties = {
  maxWidth: 820,
  margin: "0 auto",
  padding: "48px 24px",
  fontFamily: "var(--font-sans)",
};

const headerRow: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 16,
  marginBottom: 28,
};

const refreshBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "7px 12px",
  fontSize: 13,
  fontFamily: "var(--font-sans)",
  color: "var(--color-text-secondary)",
  background: "var(--color-surface-raised)",
  border: "var(--kuvio-border-default)",
  borderRadius: 8,
  cursor: "pointer",
};

const headlineRow: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 16,
  marginBottom: 24,
};

const headlineNumber: CSSProperties = {
  fontSize: 44,
  fontWeight: 600,
  lineHeight: 1,
  fontVariantNumeric: "tabular-nums",
};

const statsRow: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 12,
  marginBottom: 32,
};

const statCard: CSSProperties = {
  flex: "1 1 120px",
  padding: "12px 14px",
  background: "var(--color-surface-raised)",
  border: "var(--kuvio-border-default)",
  borderRadius: 10,
};

const sectionHeading: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-tertiary)",
  margin: "0 0 12px",
};

const chartSvg: CSSProperties = {
  width: "100%",
  height: "auto",
  display: "block",
  marginBottom: 8,
};

const legendRow: CSSProperties = {
  display: "flex",
  gap: 16,
  marginBottom: 32,
  fontSize: 12,
  color: "var(--color-text-secondary)",
};

const legendItem: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 6 };
const swatch: CSSProperties = { width: 12, height: 12, borderRadius: 3, display: "inline-block" };

const table: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

const thBase: CSSProperties = {
  padding: "8px 10px",
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  color: "var(--color-text-tertiary)",
  borderBottom: "var(--kuvio-border-default)",
};
const thLeft: CSSProperties = { ...thBase, textAlign: "left" };
const thRight: CSSProperties = { ...thBase, textAlign: "right" };

const tr: CSSProperties = { borderBottom: "1px solid var(--color-border-tertiary)" };
const tdBase: CSSProperties = { padding: "9px 10px", fontVariantNumeric: "tabular-nums" };
const tdLeft: CSSProperties = { ...tdBase, textAlign: "left", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const tdRight: CSSProperties = { ...tdBase, textAlign: "right" };

const muted: CSSProperties = {
  fontSize: 14,
  color: "var(--color-text-tertiary)",
  padding: "16px 0",
};

const emptyHint: CSSProperties = {
  fontSize: 14,
  color: "var(--color-text-secondary)",
  lineHeight: 1.5,
  padding: "16px 18px",
  background: "var(--color-surface-raised)",
  border: "var(--kuvio-border-default)",
  borderRadius: 10,
};

const errorBox: CSSProperties = {
  fontSize: 14,
  color: "var(--color-error)",
  padding: "16px 18px",
  background: "var(--color-error-container)",
  borderRadius: 10,
};
